// Email sign-up forms (newsletter, back in stock): <form data-subscribe="newsletter|notify" data-item="<handle>">.
// They post to our own sign-up service (/api/subscribe, src/server/subscribe.js), which adds the person
// to Shopify Customers with marketing consent and tags. The form only says "you're on the list" once
// that has really happened. Spam: a hidden trap field, plus Cloudflare Turnstile's invisible check.
const EMAIL = /^[^\s@"\\]+@[^\s@"\\]+\.[^\s@"\\]{2,}$/;

// ---------- Turnstile, loaded the first time someone is in an email field ----------
let tsLoad = null;
function turnstile() {
  if (!tsLoad) tsLoad = new Promise((res, rej) => {
    window.__patronusTs = () => res(window.turnstile);
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__patronusTs&render=explicit';
    s.async = true; s.onerror = () => { tsLoad = null; rej(new Error('turnstile')); };
    document.head.appendChild(s);
  });
  return tsLoad;
}
// The invisible check starts as soon as someone is in an email field, so it's done by the time they tap the
// button. A token works once and lasts 300 seconds (Turnstile's rule); an older one is replaced.
const TOKEN_TTL = 240000;
function prepareToken(form, sitekey) {
  if (form._tsP && Date.now() - form._tsAt < TOKEN_TTL) return form._tsP;
  form._tsAt = Date.now();
  const p = form._tsP = turnstile().then((ts) => new Promise((resolve, reject) => {
    let box = form.querySelector('.ts-box');
    if (!box) { box = document.createElement('div'); box.className = 'ts-box'; form.appendChild(box); }
    form._tsDone = resolve; form._tsFail = () => reject(new Error('challenge'));
    if (form._ts == null) form._ts = ts.render(box, { sitekey, execution: 'execute', appearance: 'interaction-only', callback: (t) => form._tsDone?.(t), 'error-callback': () => form._tsFail?.() });
    ts.reset(form._ts); ts.execute(form._ts);
  }));
  p.catch(() => { if (form._tsP === p) form._tsP = null; });
  return p;
}
async function humanToken(form, sitekey) {
  if (!sitekey) return '';
  const p = prepareToken(form, sitekey);
  form._tsP = null;                                   // used up: the next sign-up starts a fresh check
  return p;
}

export class SignupError extends Error { constructor(code) { super(code); this.code = code; } }
export function siteSubscriber(url = '/api/subscribe', fetchApi = (...a) => fetch(...a)) {
  return async ({ email, list, item, source, website, turnstile: token }) => {
    let r;
    try { r = await fetchApi(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, list, item, source, website, turnstile: token }) }); }
    catch { throw new SignupError('network'); }
    if (r.ok) { try { return await r.json(); } catch { return { ok: true }; } }
    let code = 'server'; try { code = (await r.json()).error || code; } catch {}
    throw new SignupError(code);
  };
}

const COPY = {
  newsletter: { ok: 'Thanks, you’re on the list.' },
  notify: { ok: 'Thanks. We’ll email you when it’s back.' },
};
const FAIL = {
  email: 'Please enter a valid email address.',
  not_configured: 'Sign-ups open soon. Please try again in a day or two.',
  challenge: 'We couldn’t check that sign-up. Please try again.',
  network: 'That didn’t go through. Check your connection and try again.',
};

export function mountForms({ subscribe, sitekey = null, toast, onCode = null, offer = null, gtag = window.gtag, doc = document }) {
  // a field people never see; bots fill it in
  doc.querySelectorAll('[data-subscribe]').forEach(f => {
    if (!f.querySelector('[name=website]')) f.insertAdjacentHTML('beforeend', '<input class="hp" type="text" data-clarity-mask="true" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">');
  });
  doc.addEventListener('focusin', (e) => { const f = sitekey && e.target.matches?.('[data-subscribe] input[type=email]') && e.target.closest('[data-subscribe]'); if (f) prepareToken(f, sitekey).catch(() => {}); });
  doc.addEventListener('submit', async (e) => {
    const f = e.target.closest('[data-subscribe]'); if (!f) return;
    e.preventDefault();
    const kind = f.dataset.subscribe === 'notify' ? 'notify' : 'newsletter';
    const input = f.querySelector('input[type=email]'), btn = f.querySelector('button'), msg = f.querySelector('.form-msg');
    const say = (text, bad) => { if (msg) { msg.textContent = text; msg.classList.toggle('bad', !!bad); } else toast?.(text); };
    const email = input.value.trim();
    if (!EMAIL.test(email)) { input.setAttribute('aria-invalid', 'true'); say(FAIL.email, true); input.focus(); return; }
    input.removeAttribute('aria-invalid');
    if (f.classList.contains('busy')) return;
    f.classList.add('busy'); btn.disabled = true; f.setAttribute('aria-busy', 'true');
    try {
      const token = await humanToken(f, sitekey).catch(() => { throw new SignupError('challenge'); });
      const res = await subscribe({ email, list: kind, item: f.dataset.item || '', source: f.dataset.source || '', website: f.querySelector('[name=website]')?.value || '', turnstile: token });
      f.classList.add('done'); f.reset();
      if (res?.code) { say(`${COPY[kind].ok} Your ${offer?.percent ? offer.percent + '% ' : ''}code is ${res.code}, and it’s already in your bag.`); onCode?.(res.code, f); }
      else say(COPY[kind].ok);
      f.dispatchEvent(new CustomEvent('subscribed', { bubbles: true, detail: { code: res?.code || null } }));
      gtag?.('event', kind === 'notify' ? 'notify_back_in_stock' : 'sign_up', { method: 'email', item_id: f.dataset.item });
    } catch (err) {
      say(FAIL[err.code] || 'Something went wrong on our side. Please try again in a minute.', true);
    } finally { f.classList.remove('busy'); btn.disabled = false; f.removeAttribute('aria-busy'); }
  });
  doc.addEventListener('input', (e) => { const f = e.target.closest('[data-subscribe]'); if (f && e.target.matches('input[aria-invalid]')) { e.target.removeAttribute('aria-invalid'); const m = f.querySelector('.form-msg'); if (m) m.textContent = ''; } });
}
